/**
 * nnr2023-data.js
 * A comprehensive, function-free dataset of the Nordic Nutrition Recommendations 2023.
 * This module structures all nutritional reference values by life stage, including explicit age ranges, BMI,
 * and official energy references (BEE/REE and EER) from the NNR2023 Appendix.
 * Last updated: 2025-11-01 (Reflects official Appendix errata)
 *
 * Notes on table numbering: On the NNR website “Energy” page these appear as Tables 8–10;
 * in Appendix 4 (post-errata) they correspond to Tables 6–7 and Table 3 for infants.
 */

module.exports = {
  meta: {
    version: "2025-11-01",
    source: "NNR2023",
    notes:
      "Consolidated data from NNR2023 tables. Each life stage includes explicit age ranges, macros, protein-by-kilogram guidance, nutrient reference values (RI, AI, UL, CDRR), BMI from SEE tables, and official energy references. Sodium uses CDRR, not UL.",
    units: {
      kcalPerGram: { protein: 4, fat: 9, carbs: 4, alcohol: 7 },
      energyNormalization: { MJ_TO_KCAL: 239, KJ_PER_MJ: 1000, KCAL_PER_KJ: 0.239 },
      bmi: "kg/m²"
    },
    energy_sources: {
      adults_table6: "NNR2023 Appendix 4 – Table 6 (adults, pregnancy, lactation)",
      children_table7: "NNR2023 Appendix 4 – Table 7 (children 1–17y)",
      infants_table3: "NNR2023 Appendix 4 – Table 3 (1–12 months, kJ/kg)"
    }
  },

  lifeStages: [
    /* ===========================
       Infants
       =========================== */
    {
      id: "infant_0_6m",
      label: "≤ 6 months",
      age_years: { min: 0, max: 0.5 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 2, max: 3 } // structural placeholder
      },
      // SEE Table 2 at 0 years (newborn)
      bmi: {
        unit: "kg/m²",
        girls_mean: 13.83,
        boys_mean: 13.90,
        pooled_mean: 13.86,
        method: "Computed from SEE Table 2 (age 0): BMI = kg / (m²)"
      },
      // Energy: NNR provides kJ/kg for 6–12 months (Appendix 4, Table 3). We expose the 6-month row here for reference.
      energy: {
        unit: { specific: "kJ/kg (per day)" },
        infants_table3_reference: true,
        kJ_per_kg_at_6m: { girls: 342, boys: 339 },
        note: "Exclusive breastfeeding predominates earlier months; NNR gives explicit energy/kg rows starting at 6 months (Appendix 4, Table 3)."
      },
      proteinByKg: { note: "Values begin at 7 months." },
      nutrients: {
        vitaminA: { unit: "µg_RE", AI: 300 },
        riboflavin: { unit: "mg", AI: 0.3 },
        vitaminB6: { unit: "mg", AI: 0.1 },
        folate: { unit: "µg", AI: 64 },
        vitaminC: { unit: "mg", AI: 30 },
        vitaminE: { unit: "mg a-TE", AI: 4 },
        vitaminK: { unit: "µg", AI: 2 },
        pantothenicAcid: { unit: "mg", AI: 2 },
        biotin: { unit: "µg", AI: 4 },
        vitaminB12: { unit: "µg", AI: 0.4 },
        choline: { unit: "mg", AI: 120 },
        calcium: { unit: "mg", AI: 120 },
        copper: { unit: "µg", AI: 200 },
        potassium: { unit: "mg", AI: 400 },
        magnesium: { unit: "mg", AI: 25 },
        iodine: { unit: "µg", AI: 85 },
        selenium: { unit: "µg", AI: 10 },
        manganese: { unit: "mg", AI_range: { min: 0.02, max: 0.5 } },
        sodium: { unit: "g", CDRR: 0.11 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },
    {
      id: "infant_7_11m",
      label: "7–11 months",
      age_years: { min: 0.5, max: 0.99 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 2, max: 3 }
      },
      // BMI via linear interpolation to ~9 months between 0y and 1y SEE Table 2 points
      bmi: {
        unit: "kg/m²",
        girls_mean: 17.09,
        boys_mean: 17.48,
        pooled_mean: 17.29,
        method: "SEE Table 2; linear interpolation of weight & height at 9 months, then BMI = kg/(m²)"
      },
      // Energy from Appendix 4, Table 3 (6–12 months), in kJ/kg. Provide endpoints + midpoint estimate.
      energy: {
        unit: { specific: "kJ/kg (per day)" },
        infants_table3_reference: true,
        kJ_per_kg_at_6m: { girls: 342, boys: 339 },
        kJ_per_kg_at_12m: { girls: 333, boys: 337 },
        est_kJ_per_kg_mid_9m: {
          girls: 337.5,
          boys: 338.0,
          derived: true,
          method: "midpoint average of 6m and 12m rows"
        },
        note: "For a fixed MJ/d, multiply kJ/kg by child body weight (kg) and divide by 1000."
      },
      proteinByKg: { AR_g_per_kg: 1.04, RI_g_per_kg: 1.23 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 250 },
        vitaminD: { unit: "µg", RI: 10, UL: 35 },
        riboflavin: { unit: "mg", RI: 0.45 },
        vitaminB6: { unit: "mg", RI: 0.45 },
        folate: { unit: "µg", RI: 90 },
        vitaminC: { unit: "mg", AI: 30 },
        vitaminE: { unit: "mg a-TE", AI: 5 },
        vitaminK: { unit: "µg", AI: 10 },
        pantothenicAcid: { unit: "mg", AI: 3 },
        biotin: { unit: "µg", AI: 5 },
        vitaminB12: { unit: "µg", AI: 1.5 },
        choline: { unit: "mg", AI: 170 },
        calcium: { unit: "mg", AI: 310 },
        iron: { unit: "mg", AI: 10 },
        zinc: { unit: "mg", AI: 3.0 },
        copper: { unit: "µg", AI: 220 },
        phosphorus: { unit: "mg", AI: 170 },
        potassium: { unit: "mg", AI: 700 },
        magnesium: { unit: "mg", AI: 80 },
        iodine: { unit: "µg", AI: 85 },
        selenium: { unit: "µg", AI: 20 },
        fluoride: { unit: "mg", AI: 0.4 },
        manganese: { unit: "mg", AI: 0.26 },
        molybdenum: { unit: "µg", AI: 10 },
        sodium: { unit: "g", CDRR: 0.37 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },

    /* ===========================
       Children & Adolescents (REE/EER: Appendix 4 – Table 7)
       =========================== */
    {
      id: "child_1_3y",
      label: "1–3 years",
      age_years: { min: 1, max: 3 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 2, max: 3 }
      },
      bmi: {
        unit: "kg/m²",
        girls_mean: 16.45,
        boys_mean: 16.83,
        pooled_mean: 16.64,
        method: "SEE Table 2; equal-weight mean of ages 1–3"
      },
      energy: {
        unit: { REE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 13.6,
        REE_MJd: 3.3,
        EER_MJd: 4.6,
        source: "Appendix 4, Table 7"
      },
      proteinByKg: { AR_g_per_kg: 0.82, RI_g_per_kg: 1.05 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 300 },
        vitaminD: { unit: "µg", RI: 10, UL: 50 },
        riboflavin: { unit: "mg", RI: 0.6 },
        vitaminB6: { unit: "mg", RI: 0.6 },
        folate: { unit: "µg", RI: 120 },
        vitaminC: { unit: "mg", RI: 25 },
        vitaminE: { unit: "mg a-TE", AI: 7 },
        vitaminK: { unit: "µg", AI: 15 },
        pantothenicAcid: { unit: "mg", AI: 4 },
        biotin: { unit: "µg", AI: 20 },
        vitaminB12: { unit: "µg", AI: 1.5 },
        choline: { unit: "mg", AI: 150 },
        calcium: { unit: "mg", RI: 450 },
        iron: { unit: "mg", RI: 7 },
        zinc: { unit: "mg", RI: 4.5 },
        copper: { unit: "µg", RI: 340 },
        phosphorus: { unit: "mg", AI: 250 },
        potassium: { unit: "mg", AI: 850 },
        magnesium: { unit: "mg", AI: 170 },
        iodine: { unit: "µg", AI: 100 },
        selenium: { unit: "µg", AI: 20 },
        fluoride: { unit: "mg", AI: 0.7 },
        manganese: { unit: "mg", AI: 0.5 },
        molybdenum: { unit: "µg", AI: 15 },
        sodium: { unit: "g", CDRR: 1.1 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },
    {
      id: "child_4_6y",
      label: "4–6 years",
      age_years: { min: 4, max: 6 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 2, max: 3 }
      },
      bmi: {
        unit: "kg/m²",
        girls_mean: 15.51,
        boys_mean: 15.57,
        pooled_mean: 15.54,
        method: "SEE Table 2; equal-weight mean of ages 4–6"
      },
      energy: {
        unit: { REE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 20.7,
        REE_MJd: 4.0,
        EER_MJd: 6.3,
        source: "Appendix 4, Table 7"
      },
      proteinByKg: { AR_g_per_kg: 0.70, RI_g_per_kg: 0.86 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 350 },
        vitaminD: { unit: "µg", RI: 10, UL: 50 },
        riboflavin: { unit: "mg", RI: 0.7 },
        vitaminB6: { unit: "mg", RI: 0.7 },
        folate: { unit: "µg", RI: 140 },
        vitaminC: { unit: "mg", RI: 35 },
        vitaminE: { unit: "mg a-TE", AI: 8 },
        vitaminK: { unit: "µg", AI: 20 },
        pantothenicAcid: { unit: "mg", AI: 4 },
        biotin: { unit: "µg", AI: 25 },
        vitaminB12: { unit: "µg", AI: 1.7 },
        choline: { unit: "mg", AI: 170 },
        calcium: { unit: "mg", RI: 800 },
        iron: { unit: "mg", RI: 7 },
        zinc: { unit: "mg", RI: 5.8 },
        copper: { unit: "µg", RI: 400 },
        phosphorus: { unit: "mg", AI: 440 },
        potassium: { unit: "mg", AI: 1150 },
        magnesium: { unit: "mg", AI: 230 },
        iodine: { unit: "µg", AI: 100 },
        selenium: { unit: "µg", AI: 25 },
        fluoride: { unit: "mg", AI: 1.0 },
        manganese: { unit: "mg", AI: 1.0 },
        molybdenum: { unit: "µg", AI: 20 },
        sodium: { unit: "g", CDRR: 1.4 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },
    {
      id: "child_7_10y",
      label: "7–10 years",
      age_years: { min: 7, max: 10 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 2, max: 3 }
      },
      bmi: {
        unit: "kg/m²",
        girls_mean: 15.95,
        boys_mean: 15.91,
        pooled_mean: 15.93,
        method: "SEE Table 2; equal-weight mean of ages 7–10"
      },
      energy: {
        unit: { REE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 30.8,
        REE_MJd: 4.9,
        EER_MJd: 7.8,
        source: "Appendix 4, Table 7"
      },
      proteinByKg: { AR_g_per_kg: 0.75, RI_g_per_kg: 0.91 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 450 },
        vitaminD: { unit: "µg", RI: 10, UL: 50 },
        riboflavin: { unit: "mg", RI: 1.0 },
        vitaminB6: { unit: "mg", RI: 1.0 },
        folate: { unit: "µg", RI: 200 },
        vitaminC: { unit: "mg", RI: 55 },
        vitaminE: { unit: "mg a-TE", AI: 9 },
        vitaminK: { unit: "µg", AI: 30 },
        pantothenicAcid: { unit: "mg", AI: 4 },
        biotin: { unit: "µg", AI: 25 },
        vitaminB12: { unit: "µg", AI: 2.5 },
        choline: { unit: "mg", AI: 250 },
        calcium: { unit: "mg", RI: 800 },
        iron: { unit: "mg", RI: 9 },
        zinc: { unit: "mg", RI: 7.7 },
        copper: { unit: "µg", RI: 570 },
        phosphorus: { unit: "mg", AI: 440 },
        potassium: { unit: "mg", AI: 1800 },
        magnesium: { unit: "mg", AI: 230 },
        iodine: { unit: "µg", AI: 100 },
        selenium: { unit: "µg", AI: 40 },
        fluoride: { unit: "mg", AI: 1.5 },
        manganese: { unit: "mg", AI: 1.5 },
        molybdenum: { unit: "µg", AI: 30 },
        sodium: { unit: "g", CDRR: 1.7 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },
    {
      id: "female_11_14y",
      label: "Female 11–14y",
      age_years: { min: 11, max: 14 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 2, max: 3 }
      },
      bmi: {
        unit: "kg/m²",
        mean: 18.39,
        method: "SEE Table 2; girls' equal-weight mean of ages 11–14"
      },
      energy: {
        unit: { REE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 46.5,
        REE_MJd: 5.4,
        EER_MJd: 9.2,
        source: "Appendix 4, Table 7"
      },
      proteinByKg: { AR_g_per_kg: 0.72, RI_g_per_kg: 0.88 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 650 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.4 },
        vitaminB6: { unit: "mg", RI: 1.3 },
        folate: { unit: "µg", RI: 280 },
        vitaminC: { unit: "mg", RI: 75 },
        vitaminE: { unit: "mg a-TE", AI: 10 },
        vitaminK: { unit: "µg", AI: 45 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 35 },
        vitaminB12: { unit: "µg", AI: 3.5 },
        choline: { unit: "mg", AI: 350 },
        calcium: { unit: "mg", RI: 1150 },
        iron: {
          unit: "mg",
          RI: 13,
          specialCases: { menstruating_RI_mg: 15 }
        },
        zinc: { unit: "mg", RI: 10.8 },
        copper: { unit: "µg", RI: 780 },
        phosphorus: { unit: "mg", AI: 640 },
        potassium: { unit: "mg", AI: 2400 },
        magnesium: { unit: "mg", AI: 250 },
        iodine: { unit: "µg", AI: 120 },
        selenium: { unit: "µg", AI: 60 },
        fluoride: { unit: "mg", AI: 2.3 },
        manganese: { unit: "mg", AI: 2.0 },
        molybdenum: { unit: "µg", AI: 50 },
        sodium: { unit: "g", CDRR: 2.0 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },
    {
      id: "male_11_14y",
      label: "Male 11–14y",
      age_years: { min: 11, max: 14 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 2, max: 3 }
      },
      bmi: {
        unit: "kg/m²",
        mean: 17.91,
        method: "SEE Table 2; boys' equal-weight mean of ages 11–14"
      },
      energy: {
        unit: { REE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 48.2,
        REE_MJd: 6.2,
        EER_MJd: 10.5,
        source: "Appendix 4, Table 7"
      },
      proteinByKg: { AR_g_per_kg: 0.74, RI_g_per_kg: 0.90 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 700 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.4 },
        vitaminB6: { unit: "mg", RI: 1.5 },
        folate: { unit: "µg", RI: 260 },
        vitaminC: { unit: "mg", RI: 80 },
        vitaminE: { unit: "mg a-TE", AI: 11 },
        vitaminK: { unit: "µg", AI: 50 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 35 },
        vitaminB12: { unit: "µg", AI: 3.0 },
        choline: { unit: "mg", AI: 330 },
        calcium: { unit: "mg", RI: 1150 },
        iron: { unit: "mg", RI: 11 },
        zinc: { unit: "mg", RI: 11.1 },
        copper: { unit: "µg", RI: 740 },
        phosphorus: { unit: "mg", AI: 640 },
        potassium: { unit: "mg", AI: 2550 },
        magnesium: { unit: "mg", AI: 300 },
        iodine: { unit: "µg", AI: 130 },
        selenium: { unit: "µg", AI: 65 },
        fluoride: { unit: "mg", AI: 2.4 },
        manganese: { unit: "mg", AI: 2.0 },
        molybdenum: { unit: "µg", AI: 45 },
        sodium: { unit: "g", CDRR: 2.0 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },
    {
      id: "female_15_17y",
      label: "Female 15–17y",
      age_years: { min: 15, max: 17 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      bmi: {
        unit: "kg/m²",
        mean: 20.63,
        method: "SEE Table 2; girls' equal-weight mean of ages 15–17"
      },
      energy: {
        unit: { REE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 57.8,
        REE_MJd: 5.9,
        EER_MJd: 10.1,
        source: "Appendix 4, Table 7"
      },
      proteinByKg: { AR_g_per_kg: 0.68, RI_g_per_kg: 0.84 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 650 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.5 },
        folate: { unit: "µg", RI: 310 },
        vitaminC: { unit: "mg", RI: 90 },
        vitaminE: { unit: "mg a-TE", AI: 11 },
        vitaminK: { unit: "µg", AI: 60 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 35 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 390 },
        calcium: { unit: "mg", RI: 1150 },
        iron: { unit: "mg", RI: 15 },
        zinc: { unit: "mg", RI: 12.2 },
        copper: { unit: "µg", RI: 880 },
        phosphorus: { unit: "mg", AI: 640 },
        potassium: { unit: "mg", AI: 2850 },
        magnesium: { unit: "mg", AI: 250 },
        iodine: { unit: "µg", AI: 120 },
        selenium: { unit: "µg", AI: 70 },
        fluoride: { unit: "mg", AI: 2.9 },
        manganese: { unit: "mg", AI: 3.0 },
        molybdenum: { unit: "µg", AI: 60 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },
    {
      id: "male_15_17y",
      label: "Male 15–17y",
      age_years: { min: 15, max: 17 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      bmi: {
        unit: "kg/m²",
        mean: 20.45,
        method: "SEE Table 2; boys' equal-weight mean of ages 15–17"
      },
      energy: {
        unit: { REE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 65.6,
        REE_MJd: 7.5,
        EER_MJd: 12.7,
        source: "Appendix 4, Table 7"
      },
      proteinByKg: { AR_g_per_kg: 0.71, RI_g_per_kg: 0.87 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 750 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.8 },
        folate: { unit: "µg", RI: 320 },
        vitaminC: { unit: "mg", RI: 105 },
        vitaminE: { unit: "mg a-TE", AI: 12 },
        vitaminK: { unit: "µg", AI: 65 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 35 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 1150 },
        iron: { unit: "mg", RI: 11 },
        zinc: { unit: "mg", RI: 14.0 },
        copper: { unit: "µg", RI: 900 },
        phosphorus: { unit: "mg", AI: 640 },
        potassium: { unit: "mg", AI: 3400 },
        magnesium: { unit: "mg", AI: 300 },
        iodine: { unit: "µg", AI: 140 },
        selenium: { unit: "µg", AI: 85 },
        fluoride: { unit: "mg", AI: 3.3 },
        manganese: { unit: "mg", AI: 2.5 },
        molybdenum: { unit: "µg", AI: 60 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: { unit: "NE/MJ", RI_per_MJ_NE: 1.6 }
      }
    },

    /* ===========================
       Adults (BEE/EER: Appendix 4 – Table 6)
       =========================== */
    {
      id: "female_18_24y",
      label: "Female 18–24y",
      age_years: { min: 18, max: 24 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1: 64.2 kg, 167 cm
      bmi: {
        unit: "kg/m²",
        mean: 23.02,
        reference: { weight_kg: 64.2, height_cm: 167 },
        source: "SEE Table 1"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 64.2,
        BEE_MJd: 5.9,
        EER_MJd_byPAL: { "1.4": 8.3, "1.6": 9.4, "1.8": 10.6 },
        source: "Appendix 4, Table 6"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 700, UL: 3000 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.6, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 95 },
        vitaminE: { unit: "mg a-TE", AI: 10, UL: 300 },
        vitaminK: { unit: "µg", AI: 65 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 1000 },
        iron: { unit: "mg", RI: 15 },
        zinc: { unit: "mg", RI: 9.7, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 550, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 300, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 75, UL: 255 },
        fluoride: { unit: "mg", AI: 3.2, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "male_18_24y",
      label: "Male 18–24y",
      age_years: { min: 18, max: 24 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1: 75.2 kg, 181 cm
      bmi: {
        unit: "kg/m²",
        mean: 22.95,
        reference: { weight_kg: 75.2, height_cm: 181 },
        source: "SEE Table 1"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 75.2,
        BEE_MJd: 7.4,
        EER_MJd_byPAL: { "1.4": 10.4, "1.6": 11.8, "1.8": 13.2 },
        source: "Appendix 4, Table 6"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 800, UL: 3000 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.8, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 110 },
        vitaminE: { unit: "mg a-TE", AI: 11, UL: 300 },
        vitaminK: { unit: "µg", AI: 75 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 1000 },
        iron: { unit: "mg", RI: 9 },
        zinc: { unit: "mg", RI: 12.7, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 550, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 350, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 90, UL: 255 },
        fluoride: { unit: "mg", AI: 3.8, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "female_25_50y",
      label: "Female 25–50y",
      age_years: { min: 25, max: 50 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1: 64.1 kg, 167 cm
      bmi: {
        unit: "kg/m²",
        mean: 22.98,
        reference: { weight_kg: 64.1, height_cm: 167 },
        source: "SEE Table 1"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 64.1,
        BEE_MJd: 5.7,
        EER_MJd_byPAL: { "1.4": 8.0, "1.6": 9.0, "1.8": 10.2 },
        source: "Appendix 4, Table 6"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 700, UL: 3000 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.6, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 95 },
        vitaminE: { unit: "mg a-TE", AI: 10, UL: 300 },
        vitaminK: { unit: "µg", AI: 65 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 15 },
        zinc: { unit: "mg", RI: 9.7, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 520, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 300, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 75, UL: 255 },
        fluoride: { unit: "mg", AI: 3.2, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "male_25_50y",
      label: "Male 25–50y",
      age_years: { min: 25, max: 50 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      bmi: {
        unit: "kg/m²",
        mean: 23.09,
        reference: { weight_kg: 74.8, height_cm: 180 },
        source: "SEE Table 1"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 74.8,
        BEE_MJd: 7.1,
        EER_MJd_byPAL: { "1.4": 9.9, "1.6": 11.3, "1.8": 12.7 },
        source: "Appendix 4, Table 6"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 800, UL: 3000 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.8, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 110 },
        vitaminE: { unit: "mg a-TE", AI: 11, UL: 300 },
        vitaminK: { unit: "µg", AI: 75 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 9 },
        zinc: { unit: "mg", RI: 12.7, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 520, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 350, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 90, UL: 255 },
        fluoride: { unit: "mg", AI: 3.7, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "female_51_70y",
      label: "Female 51–70y",
      age_years: { min: 51, max: 70 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1: 62.5 kg, 165 cm
      bmi: {
        unit: "kg/m²",
        mean: 22.96,
        reference: { weight_kg: 62.5, height_cm: 165 },
        source: "SEE Table 1"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 62.5,
        BEE_MJd: 5.2,
        EER_MJd_byPAL: { "1.4": 7.2, "1.6": 8.3, "1.8": 9.3 },
        source: "Appendix 4, Table 6"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 700, UL: 3000 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.6, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 95 },
        vitaminE: { unit: "mg a-TE", AI: 9, UL: 300 },
        vitaminK: { unit: "µg", AI: 60 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 8 },
        zinc: { unit: "mg", RI: 9.5, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 520, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 300, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 75, UL: 255 },
        fluoride: { unit: "mg", AI: 3.1, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "male_51_70y",
      label: "Male 51–70y",
      age_years: { min: 51, max: 70 },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1: 73.0 kg, 178 cm
      bmi: {
        unit: "kg/m²",
        mean: 23.04,
        reference: { weight_kg: 73.0, height_cm: 178 },
        source: "SEE Table 1"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 73.0,
        BEE_MJd: 6.4,
        EER_MJd_byPAL: { "1.4": 9.0, "1.6": 10.3, "1.8": 11.6 },
        source: "Appendix 4, Table 6"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 800, UL: 3000 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.8, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 110 },
        vitaminE: { unit: "mg a-TE", AI: 11, UL: 300 },
        vitaminK: { unit: "µg", AI: 70 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 9 },
        zinc: { unit: "mg", RI: 12.4, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 520, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 350, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 90, UL: 255 },
        fluoride: { unit: "mg", AI: 3.7, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "female_gt70y",
      label: "Female >70y",
      age_years: { min: 71, max: null },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1: 60.6 kg, 162 cm
      bmi: {
        unit: "kg/m²",
        mean: 23.09,
        reference: { weight_kg: 60.6, height_cm: 162 },
        source: "SEE Table 1"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 60.6,
        BEE_MJd: 5.1,
        EER_MJd_byPAL: { "1.4": 7.1, "1.6": 8.2, "1.8": 9.2 },
        source: "Appendix 4, Table 6"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 650, UL: 3000 },
        vitaminD: {
          unit: "µg",
          RI: 10,
          UL: 100,
          note: "RI is 20µg for ages 75+"
        },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.6, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 95 },
        vitaminE: { unit: "mg a-TE", AI: 9, UL: 300 },
        vitaminK: { unit: "µg", AI: 60 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 7 },
        zinc: { unit: "mg", RI: 9.3, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 520, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 300, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 75, UL: 255 },
        fluoride: { unit: "mg", AI: 3.0, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "male_gt70y",
      label: "Male >70y",
      age_years: { min: 71, max: null },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1: 70.6 kg, 175 cm
      bmi: {
        unit: "kg/m²",
        mean: 23.05,
        reference: { weight_kg: 70.6, height_cm: 175 },
        source: "SEE Table 1"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 70.6,
        BEE_MJd: 6.3,
        EER_MJd_byPAL: { "1.4": 8.8, "1.6": 10.1, "1.8": 11.3 },
        source: "Appendix 4, Table 6"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 750, UL: 3000 },
        vitaminD: {
          unit: "µg",
          RI: 10,
          UL: 100,
          note: "RI is 20µg for ages 75+"
        },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.7, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 110 },
        vitaminE: { unit: "mg a-TE", AI: 11, UL: 300 },
        vitaminK: { unit: "µg", AI: 70 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 9 },
        zinc: { unit: "mg", RI: 12.1, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 520, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 350, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 85, UL: 255 },
        fluoride: { unit: "mg", AI: 3.5, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },

    /* ===========================
       ≥75y (explicit copy with vitamin D RI = 20 µg)
       =========================== */
    {
      id: "female_75plus",
      label: "Female ≥75y",
      age_years: { min: 75, max: null },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      bmi: {
        unit: "kg/m²",
        mean: 23.09,
        reference: { weight_kg: 60.6, height_cm: 162 },
        source: "SEE Table 1 (>70y baseline applied)"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 60.6,
        BEE_MJd: 5.1,
        EER_MJd_byPAL: { "1.4": 7.1, "1.6": 8.2, "1.8": 9.2 },
        source: "Appendix 4, Table 6 (>70y row applied)"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 650, UL: 3000 },
        vitaminD: { unit: "µg", RI: 20, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.6, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 95 },
        vitaminE: { unit: "mg a-TE", AI: 9, UL: 300 },
        vitaminK: { unit: "µg", AI: 60 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 7 },
        zinc: { unit: "mg", RI: 9.3, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 520, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 300, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 75, UL: 255 },
        fluoride: { unit: "mg", AI: 3.0, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "male_75plus",
      label: "Male ≥75y",
      age_years: { min: 75, max: null },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      bmi: {
        unit: "kg/m²",
        mean: 23.05,
        reference: { weight_kg: 70.6, height_cm: 175 },
        source: "SEE Table 1 (>70y baseline applied)"
      },
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 70.6,
        BEE_MJd: 6.3,
        EER_MJd_byPAL: { "1.4": 8.8, "1.6": 10.1, "1.8": 11.3 },
        source: "Appendix 4, Table 6 (>70y row applied)"
      },
      proteinByKg: { AR_g_per_kg: 0.66, RI_g_per_kg: 0.83 },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 750, UL: 3000 },
        vitaminD: { unit: "µg", RI: 20, UL: 100 },
        riboflavin: { unit: "mg", RI: 1.6 },
        vitaminB6: { unit: "mg", RI: 1.7, UL: 25 },
        folate: {
          unit: "µg",
          RI: 330,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 110 },
        vitaminE: { unit: "mg a-TE", AI: 11, UL: 300 },
        vitaminK: { unit: "µg", AI: 70 },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.0 },
        choline: { unit: "mg", AI: 400 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 9 },
        zinc: { unit: "mg", RI: 12.1, UL: 25 },
        copper: { unit: "µg", RI: 900, UL: 5000 },
        phosphorus: { unit: "mg", AI: 520, notRecommendedToExceed: 2500 },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: { unit: "mg", AI: 350, UL_fromSupplementsOnly: 250 },
        iodine: { unit: "µg", AI: 150, UL: 600 },
        selenium: { unit: "µg", AI: 85, UL: 255 },
        fluoride: { unit: "mg", AI: 3.5, UL_range: { min: 3.5, max: 10 } },
        manganese: { unit: "mg", AI: 3.0, UL: null, UL_note: "insufficient basis" },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },

    /* ===========================
       Pregnancy & Lactation (Table 6)
       =========================== */
    {
      id: "pregnancy",
      label: "Pregnancy",
      condition: "pregnancy",
      age_years: { note: "Applies to any age during pregnancy." },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1 pregnancy reference: 75.2 kg, 167 cm
      bmi: {
        unit: "kg/m²",
        mean: 26.96,
        reference: { weight_kg: 75.2, height_cm: 167 },
        source: "SEE Table 1"
      },
      // Appendix 4 – pregnancy line (PAL 1.4 and 1.6)
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceHeight_cm: 167,
        referenceWeight_kg_by_trimester: { T1: 64.9, T2: 69.0, T3: 75.2 },
        BEE_MJd: 10.2,
        EER_MJd_byPAL: { "1.4": 11.3, "1.6": 12.5 },
        source: "Appendix 4, Table 6",
        note: "Official pregnancy line corresponds to mid–third trimester reference weight (BMI 23)."
      },
      proteinByKg: {
        base_AR_g_per_kg: 0.66,
        base_RI_g_per_kg: 0.83,
        additional_g_per_day: {
          AR: [0.5, 7.2, 23],
          RI: [1, 9, 28],
          note: "Trimesters 1, 2, 3"
        }
      },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 750, UL: 3000 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: { T1: 1.6, T2: 1.7, T3: 1.8 } },
        vitaminB6: { unit: "mg", RI: { T1: 1.6, T2: 1.8, T3: 2.0 }, UL: 25 },
        folate: {
          unit: "µg",
          AI: 600,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 105 },
        vitaminE: { unit: "mg a-TE", AI: { T1: 10, T2: 11, T3: 12 }, UL: 300 },
        vitaminK: { unit: "µg", AI: { T1: 65, T2: 70, T3: 75 } },
        pantothenicAcid: { unit: "mg", AI: 5 },
        biotin: { unit: "µg", AI: 40 },
        vitaminB12: { unit: "µg", AI: 4.5 },
        choline: { unit: "mg", AI: { T1: 410, T2: 430, T3: 470 } },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: { T1: 24, T2: 25, T3: 26 } },
        zinc: { unit: "mg", RI: { T1: 9.7, T2: 12.1, T3: 12.1 }, UL: 25 },
        copper: { unit: "µg", RI: 1000, UL: 5000 },
        phosphorus: {
          unit: "mg",
          AI: 520,
          notRecommendedToExceed: 2500
        },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: {
          unit: "mg",
          AI: 300,
          UL_fromSupplementsOnly: 250
        },
        iodine: {
          unit: "µg",
          AI: { T1: 175, T2: 200, T3: 200 },
          UL: 600
        },
        selenium: {
          unit: "µg",
          AI: { T1: 80, T2: 85, T3: 90 },
          UL: 255
        },
        fluoride: {
          unit: "mg",
          AI: 3.2,
          UL_range: { min: 3.5, max: 10 }
        },
        manganese: {
          unit: "mg",
          AI: 3.0,
          UL: null,
          UL_note: "insufficient basis"
        },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    },
    {
      id: "lactation",
      label: "Lactation",
      condition: "lactation",
      age_years: { note: "Applies to any age during lactation." },
      macros: {
        ranges_Epct: {
          protein: { min: 10, max: 20 },
          fat: { min: 25, max: 40 },
          carbs: { min: 45, max: 60 }
        },
        fibrePerMJ_g: { min: 3 }
      },
      // SEE Table 1 lactation reference: 64.1 kg, 167 cm
      bmi: {
        unit: "kg/m²",
        mean: 22.98,
        reference: { weight_kg: 64.1, height_cm: 167 },
        source: "SEE Table 1"
      },
      // Appendix 4 – Table 6 (≤50 y) shows BEE and EER for PAL 1.4 and 1.6
      energy: {
        unit: { BEE: "MJ/day", EER: "MJ/day" },
        referenceWeight_kg: 64.1,
        BEE_MJd: 9.9,
        EER_MJd_byPAL: { "1.4": 11.0, "1.6": 12.2 },
        source: "Appendix 4, Table 6 (≤50y)",
        note: "Appendix 4 provides EER for PAL 1.4 and 1.6 during lactation."
      },
      proteinByKg: {
        base_AR_g_per_kg: 0.66,
        base_RI_g_per_kg: 0.83,
        additional_g_per_day: {
          AR: { months0_6: 10, months_gt6: 15 },
          RI: { months0_6: 13, months_gt6: 19 }
        }
      },
      nutrients: {
        vitaminA: { unit: "µg_RE", RI: 1400, UL: 3000 },
        vitaminD: { unit: "µg", RI: 10, UL: 100 },
        riboflavin: { unit: "mg", RI: 2.0 },
        vitaminB6: { unit: "mg", RI: 1.7, UL: 25 },
        folate: {
          unit: "µg",
          RI: 490,
          UL: 1000,
          UL_appliesTo: "folic acid from supplements/fortification"
        },
        vitaminC: { unit: "mg", RI: 155 },
        vitaminE: { unit: "mg a-TE", AI: 11, UL: 300 },
        vitaminK: { unit: "µg", AI: 65 },
        pantothenicAcid: { unit: "mg", AI: 7 },
        biotin: { unit: "µg", AI: 45 },
        vitaminB12: { unit: "µg", AI: 5.5 },
        choline: { unit: "mg", AI: 520 },
        calcium: { unit: "mg", RI: 950 },
        iron: { unit: "mg", RI: 15 },
        zinc: { unit: "mg", RI: 12.6, UL: 25 },
        copper: { unit: "µg", RI: 1300, UL: 5000 },
        phosphorus: {
          unit: "mg",
          AI: 520,
          notRecommendedToExceed: 2500
        },
        potassium: { unit: "mg", AI: 3500 },
        magnesium: {
          unit: "mg",
          AI: 300,
          UL_fromSupplementsOnly: 250
        },
        iodine: { unit: "µg", AI: 200, UL: 600 },
        selenium: { unit: "µg", AI: 85, UL: 255 },
        fluoride: {
          unit: "mg",
          AI: 3.2,
          UL_range: { min: 3.5, max: 10 }
        },
        manganese: {
          unit: "mg",
          AI: 3.0,
          UL: null,
          UL_note: "insufficient basis"
        },
        molybdenum: { unit: "µg", AI: 65, UL: 600 },
        sodium: { unit: "g", CDRR: 2.3 },
        thiamin: { unit: "mg/MJ", RI_per_MJ: 0.1 },
        niacin: {
          unit: "NE/MJ",
          RI_per_MJ_NE: 1.6,
          UL_formSpecific_mg: { nicotinicAcid: 10, nicotinamide: 900 }
        }
      }
    }
  ]
};
